import React, { useEffect, useMemo } from "react"

import {
    FeatureFlag,
    FeatureFlagsExistenceLogicalOperator,
} from "@common-shared/config/featureFlags.config.ts"
import { QuotaUsageType } from "@common-shared/config/oneVerify/oneVerifyCoverage.config.ts"

import { PrimaryText } from "@front-shared/elements/primaryText.element.tsx"
import { ConfirmationHelper } from "@front-shared/helpers/confirmation.helper.tsx"

import {
    CHECKER_CONFIG,
    CoverageCheckerMode,
} from "../../config/checker.config"
import { CSV_CHECKER_CONFIG } from "../../config/csvChecker.config"
import { FeatureFlagsHelper } from "../../helpers/featureFlags.helper"
import { useAppDispatch } from "../../store/appDispatch.hook"
import { useAppSelector } from "../../store/selectors/app.selector"
import { checkerSelector } from "../../store/selectors/checker.selector"
import { runtimeSelector } from "../../store/selectors/runtime.selector"
import {
    checkerReset,
    checkerSetCsvCheckType,
    checkerSetManualCheckType,
} from "../../store/slices/checker.slice"
import type { ICheckerTypeTab } from "../../types/objects.types.ts"

import closeIcon from "@front-shared/assets/images/icons/close-input-control.svg"

export const CheckTypeSelectorComponent: React.FunctionComponent<
    ICheckTypeSelectorComponentProps
> = props => {
    const dispatch = useAppDispatch()

    const runtimeState = useAppSelector(runtimeSelector)

    const {
        checkerMode,
        modalityCode: selectedModalityCode,
        manualCheckType: selectedManualCheckType,
        csvCheckType: selectedCsvCheckType,
        isCheckTypeRelatedDataEntered,
    } = useAppSelector(checkerSelector)

    const isCsvTypeSelector = useMemo<boolean>(() => {
        return checkerMode === CoverageCheckerMode.CSV
    }, [checkerMode])

    const selectedCheckType = useMemo<QuotaUsageType | undefined>(() => {
        return isCsvTypeSelector
            ? selectedCsvCheckType
            : selectedManualCheckType
    }, [selectedManualCheckType, selectedCsvCheckType, isCsvTypeSelector])

    const availableTabs = useMemo<ICheckerTypeTab[]>(() => {
        const allTabs = isCsvTypeSelector
            ? CSV_CHECKER_CONFIG.typeTabs
            : CHECKER_CONFIG.manualTypeTabs

        return (
            allTabs
                .filter(item => {
                    if (!runtimeState.enabledFeatureFlags) {
                        return false
                    }

                    return item?.featureFlag
                        ? FeatureFlagsHelper.isFeatureFlagEnabled(
                              runtimeState.enabledFeatureFlags,
                              item.featureFlag,
                              item.featureFlagExistenceOperator ||
                                  FeatureFlagsExistenceLogicalOperator.OR,
                          )
                        : true
                })
                // Not all check types are available for all modality codes
                .filter(item => {
                    return (
                        !selectedModalityCode ||
                        !item.limitedToModalityCodes?.length ||
                        item.limitedToModalityCodes.includes(
                            selectedModalityCode,
                        )
                    )
                })
        )
    }, [
        runtimeState.enabledFeatureFlags,
        isCsvTypeSelector,
        selectedModalityCode,
    ])

    // If only one tab is available, do not show the selector
    // And select that type in state
    useEffect(() => {
        if (
            !availableTabs.length ||
            !runtimeState.enabledFeatureFlags ||
            !selectedCheckType
        ) {
            return
        }

        let isSelectedTabAllowedByPermissions = false

        switch (selectedCheckType) {
            case QuotaUsageType.GENERAL:
                isSelectedTabAllowedByPermissions =
                    FeatureFlagsHelper.isFeatureFlagEnabled(
                        runtimeState.enabledFeatureFlags,
                        isCsvTypeSelector
                            ? FeatureFlag.ONE_VERIFY_STANDARD_BULK_CHECKS
                            : FeatureFlag.ONE_VERIFY_STANDARD_MANUAL_CHECK,
                    )

                break

            case QuotaUsageType.MEDICAID:
                isSelectedTabAllowedByPermissions =
                    FeatureFlagsHelper.isFeatureFlagEnabled(
                        runtimeState.enabledFeatureFlags,
                        isCsvTypeSelector
                            ? FeatureFlag.ONE_VERIFY_MEDICAID_BULK_CHECKS
                            : FeatureFlag.ONE_VERIFY_MEDICAID_MANUAL_CHECK,
                    )

                break

            case QuotaUsageType.DISCOVER:
                isSelectedTabAllowedByPermissions =
                    FeatureFlagsHelper.isFeatureFlagEnabled(
                        runtimeState.enabledFeatureFlags,
                        isCsvTypeSelector
                            ? FeatureFlag.ONE_VERIFY_DISCOVER_BULK_CHECKS
                            : FeatureFlag.ONE_VERIFY_DISCOVER_MANUAL_CHECK,
                    )

                break

            case QuotaUsageType.SMART_SCAN:
                isSelectedTabAllowedByPermissions =
                    FeatureFlagsHelper.isFeatureFlagEnabled(
                        runtimeState.enabledFeatureFlags,
                        isCsvTypeSelector
                            ? FeatureFlag.ONE_VERIFY_SCAN_BULK_CHECKS
                            : FeatureFlag.ONE_VERIFY_SCAN_MANUAL_CHECK,
                    )

                break

            case QuotaUsageType.AGENTIC_PORTAL:
                isSelectedTabAllowedByPermissions =
                    FeatureFlagsHelper.isFeatureFlagEnabled(
                        runtimeState.enabledFeatureFlags,
                        isCsvTypeSelector
                            ? FeatureFlag.ONE_VERIFY_AGENTIC_PORTAL_BULK_CHECKS
                            : FeatureFlag.ONE_VERIFY_AGENTIC_PORTAL_MANUAL_CHECK,
                    )

                break

            case QuotaUsageType.AGENTIC_PHONE:
                isSelectedTabAllowedByPermissions =
                    FeatureFlagsHelper.isFeatureFlagEnabled(
                        runtimeState.enabledFeatureFlags,
                        isCsvTypeSelector
                            ? FeatureFlag.ONE_VERIFY_AGENTIC_PHONE_BULK_CHECKS
                            : FeatureFlag.ONE_VERIFY_AGENTIC_PHONE_MANUAL_CHECK,
                    )

                break
        }

        // If selected check type is not allowed by permissions or it's not available then reset
        if (
            !isSelectedTabAllowedByPermissions ||
            !availableTabs.some(tab => tab.key === selectedCheckType)
        ) {
            onSelected(availableTabs[0].key)
        }
    }, [
        runtimeState.enabledFeatureFlags,
        availableTabs,
        selectedCheckType,
        isCsvTypeSelector,
    ])

    const onSelected = (value?: QuotaUsageType) => {
        const onConfirm = () => {
            if (!value) {
                dispatch(checkerReset())
            }

            dispatch(
                isCsvTypeSelector
                    ? checkerSetCsvCheckType(value || QuotaUsageType.GENERAL)
                    : checkerSetManualCheckType(value),
            )
        }

        if (isCheckTypeRelatedDataEntered && !isCsvTypeSelector) {
            ConfirmationHelper.enableConfirmation(
                onConfirm,
                "You will lose any unsaved data",
                value
                    ? "By choosing one of the check type templates, you will lose any unsaved data that you’ve already filled out."
                    : "By removing check type template, you will lose any unsaved data that you’ve already filled out.",
                undefined,
                "Proceed",
                "Never mind",
                undefined,
                {
                    isLeftAligned: true,
                    withCloseIcon: true,
                    width: 530,
                },
            )
        } else {
            onConfirm()
        }
    }

    return (
        <div
            className={`
                relative p-[16px] bg-nirvana-check-type-gradient
                ${props.className}
            `}
        >
            <PrimaryText size={12} lineHeight={15} weight="medium">
                Check Types
            </PrimaryText>

            <div className="mt-[12px]">
                {availableTabs.map((tab, index) => {
                    const isSelected = tab.key === selectedCheckType

                    return (
                        <div
                            key={index}
                            className={`
                                relative px-[12px] pb-[14px] pt-[18px] overflow-hidden
                                ${
                                    isSelected
                                        ? "bg-brand-white rounded-[4px]"
                                        : "bg-transparent cursor-pointer"
                                }
                            `}
                            onClick={
                                !isSelected
                                    ? () => {
                                          onSelected(tab.key)
                                      }
                                    : undefined
                            }
                        >
                            <PrimaryText
                                typography="buttonText"
                                weight={isSelected ? "semibold" : "normal"}
                            >
                                {tab.label}
                            </PrimaryText>

                            {isSelected ? (
                                <>
                                    <div className="absolute left-0 z-10 top-0 w-[2px] h-full bg-brand-primary" />

                                    {!isCsvTypeSelector ? (
                                        <div
                                            className="absolute right-0 vertical-center z-10 p-[12px] cursor-pointer"
                                            onClick={e => {
                                                e.stopPropagation()
                                                onSelected()
                                            }}
                                        >
                                            <img
                                                src={closeIcon}
                                                alt="reset"
                                                onClick={() => {
                                                    onSelected()
                                                }}
                                            />
                                        </div>
                                    ) : null}
                                </>
                            ) : null}
                        </div>
                    )
                })}
            </div>
        </div>
    )
}

interface ICheckTypeSelectorComponentProps {
    className?: string
}
